'use client'

import React, { useCallback, useEffect, useState } from 'react'

import type { HolidayNavTab } from '@/src/types/holidays'
import useClevertapForHolidays from '@/src/hooks/analytics/clevertap/holidays'
import {
    HOLIDAY_ACTION,
    HOLIDAY_ACTION_TYPE,
    truncateHolidayText
} from '@/src/utils/analytics/holidays/holidayEvents'

import HolidayNavTabs from './holidayNavTabs'

interface HolidayNavTabsWrapperProps {
    tabs: HolidayNavTab[]
    isMobile: boolean
}

const HolidayNavTabsWrapper: React.FC<HolidayNavTabsWrapperProps> = ({ tabs, isMobile }) => {
    const [activeId, setActiveId] = useState(tabs[0]?.id ?? '')
    const { triggerHolidayUIActionEvent } = useClevertapForHolidays()

    // Scroll spy. The rootMargin pins the "active" line just below the sticky bar, so a section
    // becomes active as its heading reaches the bar rather than when it merely enters the viewport.
    useEffect(() => {
        if (typeof window === 'undefined' || !tabs.length) return

        const elements = tabs
            .map(tab => document.getElementById(tab.id))
            .filter((element): element is HTMLElement => element !== null)

        if (!elements.length) return

        const observer = new IntersectionObserver(
            entries => {
                const visible = entries
                    .filter(entry => entry.isIntersecting)
                    .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)[0]
                if (visible?.target.id) setActiveId(visible.target.id)
            },
            { rootMargin: '-96px 0px -55% 0px', threshold: 0 }
        )

        elements.forEach(element => observer.observe(element))
        return () => observer.disconnect()
    }, [tabs])

    // Only a click is an engagement — the scroll spy moves `activeId` on its own as the reader
    // scrolls, and reporting that would make every page load look like a dozen engagements.
    const handleTabClick = useCallback(
        (id: string) => {
            setActiveId(id)
            document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' })

            const tab = tabs.find(item => item.id === id)
            triggerHolidayUIActionEvent(HOLIDAY_ACTION.CONTENT_ENGAGED, {
                action_type: HOLIDAY_ACTION_TYPE.CONTENT,
                component: id,
                engagement_type: 'nav_tab',
                component_label: tab ? truncateHolidayText(tab.label) : 'N/A'
            })
        },
        [tabs, triggerHolidayUIActionEvent]
    )

    if (tabs.length < 2) return null

    // A stale activeId (a section that was filtered out) would leave MUI Tabs with no match and
    // log a value warning, so fall back to the first tab.
    const resolvedActiveId = tabs.some(tab => tab.id === activeId) ? activeId : tabs[0].id

    // Up to six labels fit across the band at desktop width; beyond that they must scroll or each
    // one is squeezed to an unreadable stub.
    const spread = !isMobile && tabs.length <= 6

    return (
        <HolidayNavTabs
            tabs={tabs}
            activeId={resolvedActiveId}
            onTabClick={handleTabClick}
            isMobile={isMobile}
            spread={spread}
        />
    )
}

export { HolidayNavTabsWrapper }
export default HolidayNavTabsWrapper
